<script>
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// Canny injects its unread badge INSIDE the [data-canny-changelog] element:
//   <button data-canny-changelog><div class="Canny_BadgeContainer">…</div></button>
// Here that element lives inside a dropdown that is hidden until the user opens
// it, so the badge would never be seen — and an unseen badge defeats the point
// of the changelog, which is to surface news to people who weren't looking for
// it. We watch the trigger and mirror the badge as a dot on the rail button.
//
// This reads DOM the Canny SDK owns rather than a supported API (initChangelog
// exposes only appID/position/align/theme). If Canny renames the class the dot
// stops appearing — the changelog itself keeps working.
const CANNY_BADGE_SELECTOR = '.Canny_Badge';
// Only used when the account's menu carries no product-feedback item, so the
// rail still shows something meaningful for the changelog on its own.
const RAIL_FALLBACK_ICON = 'sparkle';
// `sparkle` is drawn on Fluent's 16 grid (its path spans 2..14), unlike the
// rail's other glyphs, which are 24. fluent-icon-v2 defaults to a 24 viewBox,
// which would render it at 2/3 scale pinned to the top-left of the box instead
// of centred. Same convention SecondaryNavItem applies to the claudia set.
const SPARKLE_VIEW_BOX = '0 0 16 16';
const DEFAULT_VIEW_BOX = '0 0 24 24';

export default {
  name: 'CannyProductMenu',
  components: {
    WootDropdownMenu,
    WootDropdownItem,
  },
  props: {
    // The `product-feedback` entry from the sidebar config, or null when the
    // account's menu doesn't carry it.
    productFeedbackItem: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isOpen: false,
      hasUnread: false,
      menuStyle: {},
    };
  },
  computed: {
    // Reuses the icon and label the product-feedback nav item already had, so
    // the rail keeps a name people have seen before and no new copy is coined.
    // `invention` only exists in the v2 icon set — render it with
    // <fluent-icon-v2>, not <fluent-icon>.
    railIcon() {
      return this.productFeedbackItem?.icon || RAIL_FALLBACK_ICON;
    },
    // Only the sparkle fallback needs the 16 grid; the product-feedback glyph
    // (`invention`) is a regular 24.
    railIconViewBox() {
      return this.railIcon === RAIL_FALLBACK_ICON
        ? SPARKLE_VIEW_BOX
        : DEFAULT_VIEW_BOX;
    },
    railLabel() {
      const label = this.productFeedbackItem?.label;
      return label ? this.$t(`SIDEBAR.${label}`) : this.$t('SIDEBAR.CHANGELOG');
    },
  },
  mounted() {
    this.watchBadge();
    // This component owns the [data-canny-changelog] element, so it is the
    // precise moment the trigger becomes bindable. CannyChangelogWidget
    // re-runs initChangelog on this event; both ends are idempotent.
    this.$emitter.emit(BUS_EVENTS.CANNY_CHANGELOG_TRIGGER_MOUNTED);
  },
  beforeDestroy() {
    if (this.badgeObserver) this.badgeObserver.disconnect();
  },
  methods: {
    // The dropdown renders with v-show (not v-if) precisely so this element
    // exists at initChangelog time: Canny binds to whatever carries the
    // attribute when it runs, and can only inject the badge into a node that
    // is already in the document.
    watchBadge() {
      const trigger = this.$refs.changelogTrigger;
      if (!trigger || typeof MutationObserver === 'undefined') return;
      const sync = () => {
        this.hasUnread = Boolean(trigger.querySelector(CANNY_BADGE_SELECTOR));
      };
      this.badgeObserver = new MutationObserver(sync);
      this.badgeObserver.observe(trigger, { childList: true, subtree: true });
      sync();
    },
    toggle() {
      if (!this.isOpen) this.positionMenu();
      this.isOpen = !this.isOpen;
    },
    // The rail's nav list is `overflow-y-auto`, and a scroll container clips on
    // BOTH axes — an absolutely positioned dropdown reaching to the right of
    // the 64px rail would be cut off. Position it as `fixed`, measured from the
    // trigger, so it escapes that clipping context. Anchored bottom-left of the
    // button so it grows upward, matching the changelog popover's direction.
    positionMenu() {
      const rail = this.$refs.railButton;
      if (!rail || typeof rail.getBoundingClientRect !== 'function') return;
      const rect = rail.getBoundingClientRect();
      this.menuStyle = {
        position: 'fixed',
        left: `${Math.round(rect.right + 8)}px`,
        bottom: `${Math.round(window.innerHeight - rect.bottom)}px`,
      };
    },
    // Unlike OptionsMenu — which renders with v-if and therefore has no
    // click-away listener while closed — this dropdown is always in the DOM
    // (v-show, so Canny can bind to the trigger before it is ever shown). That
    // keeps the listener live even while closed, so the very click that opens
    // the menu also reaches it. Ignore events coming from our own rail button.
    close(event) {
      const rail = this.$refs.railButton;
      if (event && rail && rail.contains(event.target)) return;
      this.isOpen = false;
    },
    // Deliberately does NOT close the dropdown: Canny anchors its popover to
    // the trigger element, so hiding the dropdown would pull the anchor out of
    // the layout while the popover is open. The click-away handler closes both.
    onChangelogClick() {
      this.hasUnread = false;
    },
  },
};
</script>

<template>
  <div class="relative">
    <button
      ref="railButton"
      v-tooltip.right="{
        content: railLabel,
        classes: 'nav-rail-tooltip',
        boundariesElement: 'window',
      }"
      type="button"
      data-canny-changelog-anchor
      class="text-slate-700 dark:text-slate-100 w-10 h-10 my-2 flex items-center justify-center rounded-lg hover:bg-gray-gray-2 dark:hover:bg-dark-dark-4 dark:hover:text-basic-primaryText hover:text-basic-primaryText relative"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="menu"
      @click="toggle"
    >
      <fluent-icon-v2 :icon="railIcon" :view-box="railIconViewBox" />
      <span
        v-if="hasUnread"
        data-testid="canny-unread-dot"
        class="absolute w-2 h-2 rounded-full top-1 right-1 bg-red-500"
      />
      <span class="sr-only">{{ railLabel }}</span>
    </button>

    <div
      v-show="isOpen"
      v-on-clickaway="close"
      :style="menuStyle"
      class="z-30 w-56 px-2 py-2 bg-white border rounded-md shadow-xl dark:bg-slate-800 border-slate-25 dark:border-slate-700"
    >
      <WootDropdownMenu>
        <WootDropdownItem>
          <button
            ref="changelogTrigger"
            type="button"
            data-canny-changelog
            class="w-full h-8 bg-white button small clear secondary dark:bg-slate-800"
            @click="onChangelogClick"
          >
            <fluent-icon-v2
              icon="sparkle"
              size="14"
              view-box="0 0 16 16"
              class="icon icon--font"
            />
            <span class="button__content">
              {{ $t('SIDEBAR.CHANGELOG') }}
            </span>
          </button>
        </WootDropdownItem>
        <WootDropdownItem v-if="productFeedbackItem">
          <a
            :href="productFeedbackItem.toState"
            target="_blank"
            rel="noopener noreferrer"
            class="h-8 bg-white button small clear secondary dark:bg-slate-800"
            @click="close"
          >
            <fluent-icon-v2
              :icon="productFeedbackItem.icon"
              size="14"
              class="icon icon--font"
            />
            <span class="button__content">
              {{ $t(`SIDEBAR.${productFeedbackItem.label}`) }}
            </span>
          </a>
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
  </div>
</template>
